import { memo } from "react";

import { type TTailwindString, tw } from "classnames";

const placeholderCards = [0, 1, 2, 3];

const styles = {
  wrapper: tw("overflow-hidden", "bg-background-primary", "text-primary"),
  section: tw(
    "max-w-screen-xl",
    "mx-auto",
    "relative",
    "overflow-visible",
    "py-10",
    "md:py-16"
  ),
  content: tw("px-6", "md:px-12"),
  shellHeader: tw("relative", "mb-6", "md:mb-6", "md:pr-44"),
  base: tw("animate-pulse", "relative", "overflow-visible"),
  header: tw("mb-0"),
  tabRow: tw("flex", "gap-6", "md:gap-8", "pb-2"),
  tabRowWithLabels: tw(
    "flex",
    "gap-6",
    "md:gap-8",
    "pb-2",
    "overflow-x-auto",
    "overscroll-x-contain",
    "[touch-action:pan-x]" as TTailwindString,
    "[-webkit-overflow-scrolling:touch]" as TTailwindString,
    "w-[calc(100%+1.5rem)]" as TTailwindString,
    "md:w-full",
    "pr-6",
    "md:pr-0"
  ),
  activeTab: tw(
    "h-10",
    "w-[155px]" as TTailwindString,
    "border-0",
    "border-b-2",
    "border-solid",
    "border-placeholder",
    "md:h-12",
    "md:w-[207px]" as TTailwindString
  ),
  inactiveTab: tw(
    "h-10",
    "w-[149px]" as TTailwindString,
    "rounded-full",
    "bg-placeholder",
    "md:h-12",
    "md:w-[199px]" as TTailwindString
  ),
  tabOption: {
    active: tw(
      "box-border",
      "flex",
      "shrink-0",
      "flex-col",
      "items-center",
      "justify-center",
      "gap-2",
      "whitespace-nowrap",
      "text-center",
      "pb-1",
      "md:pb-2",
      "-mb-px",
      "border-0",
      "border-b-2",
      "border-solid",
      "border-char-900"
    ),
    inactive: tw(
      "box-border",
      "flex",
      "shrink-0",
      "flex-col",
      "items-center",
      "justify-center",
      "gap-2",
      "whitespace-nowrap",
      "text-center",
      "pb-1",
      "md:pb-2"
    ),
  },
  tabLabel: {
    active: tw(
      "font-chelt",
      "text-4xl",
      "font-light",
      "leading-tight",
      "md:text-6xl",
      "text-char-900"
    ),
    inactive: tw(
      "font-chelt",
      "text-4xl",
      "font-light",
      "leading-tight",
      "md:text-6xl",
      "text-char-600"
    ),
  },
  carousel: tw("relative", "overflow-visible", "mt-2", "md:mt-4"),
  cardRow: tw("flex", "gap-4", "overflow-visible", "md:gap-8"),
  arrows: tw(
    "hidden",
    "md:flex",
    "absolute",
    "right-0",
    "md:-right-44",
    "-top-[86px]" as TTailwindString,
    "gap-6"
  ),
  arrow: tw(
    "h-12",
    "w-12",
    "lg:h-16",
    "lg:w-16",
    "rounded-full",
    "border",
    "border-solid",
    "border-placeholder",
    "bg-background-primary"
  ),
  card: tw(
    "w-[213px]" as TTailwindString,
    "h-[397px]" as TTailwindString,
    "shrink-0",
    "md:h-[627px]" as TTailwindString,
    "md:w-[448px]" as TTailwindString
  ),
  image: tw("aspect-square", "w-full", "bg-placeholder"),
  cardContent: tw("mt-2", "flex", "flex-col", "gap-2", "md:mt-4"),
  title: tw("h-4", "w-3/4", "rounded-full", "bg-placeholder", "md:h-5"),
  description: tw("h-3", "w-full", "rounded-full", "bg-placeholder", "md:h-4"),
  price: tw("h-3", "w-24", "rounded-full", "bg-placeholder", "md:h-4"),
  swatches: tw("mt-1", "flex", "gap-2"),
  swatch: tw(
    "h-6",
    "w-6",
    "rounded-full",
    "bg-placeholder",
    "md:h-7",
    "md:w-7"
  ),
  viewAllCard: tw(
    "flex",
    "h-[213px]" as TTailwindString,
    "w-[213px]" as TTailwindString,
    "shrink-0",
    "items-center",
    "justify-center",
    "bg-background-secondary",
    "font-calibre",
    "text-lg",
    "leading-snug",
    "text-primary",
    "underline",
    "decoration-1" as TTailwindString,
    "md:h-[448px]" as TTailwindString,
    "md:w-[448px]" as TTailwindString,
    "md:text-2xl"
  ),
};

type Props = {
  activeIndex?: number;
  includeHeader?: boolean;
  includeShell?: boolean;
  tabLabels?: string[];
  viewAllText?: string;
};

const ProductCollectionCarouselPlaceholderBase = ({
  activeIndex = 0,
  includeHeader = false,
  includeShell = true,
  tabLabels = [],
  viewAllText,
}: Props) => {
  const placeholder = (
    <div
      className={styles.base}
      data-placeholder-region="component"
      data-testid="product-collection-carousel-placeholder"
    >
      {includeHeader ? (
        <div className={styles.header}>
          {tabLabels.length ? (
            <div className={styles.tabRowWithLabels}>
              {tabLabels.map((tabLabel, index) => (
                <div
                  className={
                    index === activeIndex
                      ? styles.tabOption.active
                      : styles.tabOption.inactive
                  }
                  key={`${tabLabel}-${index}`}
                >
                  <span
                    className={
                      index === activeIndex
                        ? styles.tabLabel.active
                        : styles.tabLabel.inactive
                    }
                  >
                    {tabLabel}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.tabRow}>
              <div className={styles.activeTab}></div>
              <div className={styles.inactiveTab}></div>
            </div>
          )}
        </div>
      ) : null}
      <div className={styles.carousel}>
        <div className={styles.arrows}>
          <div className={styles.arrow}></div>
          <div className={styles.arrow}></div>
        </div>
        <div className={styles.cardRow}>
          {placeholderCards.map((card) => (
            <div className={styles.card} key={card}>
              <div className={styles.image}></div>
              <div className={styles.cardContent}>
                <div className={styles.title}></div>
                <div className={styles.description}></div>
                <div className={styles.price}></div>
                <div className={styles.swatches}>
                  <div className={styles.swatch}></div>
                  <div className={styles.swatch}></div>
                  <div className={styles.swatch}></div>
                </div>
              </div>
            </div>
          ))}
          {viewAllText ? (
            <div className={styles.viewAllCard}>{viewAllText}</div>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (!includeShell) {
    return placeholder;
  }

  return (
    <div className={styles.wrapper}>
      <section className={styles.section}>
        <div className={styles.content}>
          <div className={styles.shellHeader}>{placeholder}</div>
        </div>
      </section>
    </div>
  );
};

export const ProductCollectionCarouselPlaceholder = memo(
  ProductCollectionCarouselPlaceholderBase
);
